import { useState, useMemo } from 'react';

const DAY_RATE = 380;
const NIGHT_RATE = 420;
const TAX_RATE = 0.06;

const calcNet = (d: number, n: number) => {
  const gross = d * DAY_RATE + n * NIGHT_RATE;
  return gross - Math.round(gross * TAX_RATE);
};

const SCHEDULES = [
  {
    id: 'classic',
    title: 'Классический',
    badge: '5/2',
    desc: '20 дней · 8 ч/сут',
    icon: 'ri-sun-line',
    accent: 'amber',
    dayHours: 160,
    nightHours: 0,
  },
  {
    id: 'sixone',
    title: 'Один выходной',
    badge: '6/1',
    desc: '24 дня · 8 ч/сут',
    icon: 'ri-calendar-check-line',
    accent: 'emerald',
    dayHours: 192,
    nightHours: 0,
  },
  {
    id: 'night',
    title: 'Ночные смены',
    badge: '5/2',
    desc: '20 дней · 4 дн. + 4 ноч.',
    icon: 'ri-moon-line',
    accent: 'indigo',
    dayHours: 80,
    nightHours: 80,
  },
  {
    id: 'night-sixone',
    title: 'Ночные, 1 выходной',
    badge: '6/1',
    desc: '24 дня · 4 дн. + 4 ноч.',
    icon: 'ri-moon-foggy-line',
    accent: 'violet',
    dayHours: 96,
    nightHours: 96,
  },
];

const ACCENT_MAP: Record<string, { text: string; bg: string; border: string; ring: string; dot: string }> = {
  amber:   { text: 'text-amber-400',   bg: 'bg-amber-400/10',   border: 'border-amber-400/40',   ring: 'ring-amber-400/30',   dot: 'bg-amber-400'   },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/40', ring: 'ring-emerald-400/30', dot: 'bg-emerald-400' },
  indigo:  { text: 'text-indigo-400',  bg: 'bg-indigo-400/10',  border: 'border-indigo-400/40',  ring: 'ring-indigo-400/30',  dot: 'bg-indigo-400'  },
  violet:  { text: 'text-violet-400',  bg: 'bg-violet-400/10',  border: 'border-violet-400/40',  ring: 'ring-violet-400/30',  dot: 'bg-violet-400'  },
};

export default function SalaryCalculator() {
  const [dayHours, setDayHours] = useState(160);
  const [nightHours, setNightHours] = useState(0);
  const [activeSchedule, setActiveSchedule] = useState<string | null>('classic');

  const applySchedule = (s: typeof SCHEDULES[0]) => {
    setDayHours(s.dayHours);
    setNightHours(s.nightHours);
    setActiveSchedule(s.id);
  };

  const handleDayChange = (val: number) => { setDayHours(val); setActiveSchedule(null); };
  const handleNightChange = (val: number) => { setNightHours(val); setActiveSchedule(null); };

  const gross = useMemo(() => dayHours * DAY_RATE + nightHours * NIGHT_RATE, [dayHours, nightHours]);
  const tax   = useMemo(() => Math.round(gross * TAX_RATE), [gross]);
  const net   = useMemo(() => gross - tax, [gross, tax]);
  const totalHours = dayHours + nightHours;
  const isOverLimit = totalHours > 300;

  const dayPct   = Math.round((dayHours   / 300) * 100);
  const nightPct = Math.round((nightHours / 300) * 100);

  return (
    <div className="max-w-4xl space-y-6">

      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">Калькулятор заработной платы</h2>
        <p className="text-slate-500 text-sm">Выберите график или настройте вручную — расчёт с учётом НПД 6%</p>
      </div>

      {/* Schedules grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {SCHEDULES.map((s) => {
          const a = ACCENT_MAP[s.accent];
          const isActive = activeSchedule === s.id;
          const estNet = calcNet(s.dayHours, s.nightHours);
          return (
            <button
              key={s.id}
              onClick={() => applySchedule(s)}
              className={`relative text-left rounded-2xl border p-4 transition-all cursor-pointer group
                ${isActive
                  ? `${a.bg} ${a.border} ring-1 ${a.ring}`
                  : 'bg-[#13161f] border-[#1e2130] hover:border-slate-600 hover:bg-[#15182280]'
                }`}
            >
              {isActive && (
                <span className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center">
                  <i className={`ri-check-line text-sm ${a.text}`} />
                </span>
              )}
              <span className={`w-8 h-8 flex items-center justify-center rounded-xl mb-3 ${a.bg}`}>
                <i className={`${s.icon} text-base ${a.text}`} />
              </span>
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-sm font-semibold text-white leading-tight">{s.title}</span>
              </div>
              <div className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full mb-2 ${a.bg} ${a.text}`}>
                {s.badge}
              </div>
              <p className="text-[11px] text-slate-500 mb-3 leading-snug">{s.desc}</p>
              <div className="border-t border-white/5 pt-3">
                <p className="text-[10px] text-slate-600 mb-0.5">Ожидаемый доход</p>
                <p className={`text-sm font-bold ${a.text}`}>
                  ~{estNet.toLocaleString('ru-RU')} ₽
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Sliders + Result */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Sliders */}
        <div className="lg:col-span-3 bg-[#13161f] border border-[#1e2130] rounded-2xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">Параметры расчёта</h3>
            <span className={`text-xs px-2.5 py-1 rounded-full ${isOverLimit ? 'bg-red-500/10 text-red-400' : 'bg-white/5 text-slate-500'}`}>
              {totalHours} / 300 ч
            </span>
          </div>

          {/* Total bar */}
          <div>
            <div className="h-2 bg-[#0d0f17] rounded-full overflow-hidden flex gap-0.5">
              <div
                className="h-full bg-amber-400 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(dayPct, 100)}%` }}
              />
              <div
                className="h-full bg-indigo-400 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(nightPct, 100 - Math.min(dayPct, 100))}%` }}
              />
            </div>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-[11px] text-slate-500">Дневные</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-indigo-400" />
                <span className="text-[11px] text-slate-500">Ночные</span>
              </div>
            </div>
          </div>

          {/* Day slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-400/10">
                  <i className="ri-sun-line text-sm text-amber-400" />
                </span>
                <span className="text-sm text-slate-300">Дневные часы</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-amber-400">{dayHours}</span>
                <span className="text-xs text-slate-500">ч</span>
                <span className="text-xs text-amber-600/70 ml-1">{(dayHours * DAY_RATE).toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>
            <input
              type="range" min={0} max={300} value={dayHours}
              onChange={(e) => handleDayChange(Number(e.target.value))}
              className="w-full accent-amber-400 cursor-pointer h-1.5"
            />
            <div className="flex justify-between text-[11px] text-slate-700 mt-1.5">
              <span>0</span><span>150</span><span>300 ч</span>
            </div>
          </div>

          {/* Night slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-indigo-400/10">
                  <i className="ri-moon-line text-sm text-indigo-400" />
                </span>
                <span className="text-sm text-slate-300">Ночные часы</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-indigo-400">{nightHours}</span>
                <span className="text-xs text-slate-500">ч</span>
                <span className="text-xs text-indigo-400/70 ml-1">{(nightHours * NIGHT_RATE).toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>
            <input
              type="range" min={0} max={300} value={nightHours}
              onChange={(e) => handleNightChange(Number(e.target.value))}
              className="w-full accent-indigo-400 cursor-pointer h-1.5"
            />
            <div className="flex justify-between text-[11px] text-slate-700 mt-1.5">
              <span>0</span><span>150</span><span>300 ч</span>
            </div>
          </div>

          {isOverLimit && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2.5">
              <i className="ri-error-warning-line text-red-400 text-sm" />
              <span className="text-xs text-red-400">
                Превышен максимум 300 ч/мес (10 ч/сут × 30 дней). Суммарно: {totalHours} ч.
              </span>
            </div>
          )}
        </div>

        {/* Result */}
        <div className="lg:col-span-2 bg-[#0c1a12] border border-emerald-900/30 rounded-2xl p-6 flex flex-col justify-between">
          <div>
            <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-widest mb-5">Прогноз выплаты</p>

            <div className="space-y-3 mb-5">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Итого часов</span>
                <span className={`text-sm font-semibold ${isOverLimit ? 'text-red-400' : 'text-slate-300'}`}>
                  {totalHours} ч
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Брутто</span>
                <span className="text-sm font-semibold text-slate-300">{gross.toLocaleString('ru-RU')} ₽</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">НПД 6%</span>
                <span className="text-sm font-semibold text-red-400">−{tax.toLocaleString('ru-RU')} ₽</span>
              </div>
            </div>

            <div className="border-t border-emerald-900/30 pt-5 mb-5">
              <p className="text-xs text-emerald-700 mb-1.5">К выплате на руки</p>
              <div className="flex items-end gap-1.5">
                <span className="text-4xl font-bold text-emerald-400 leading-none">
                  {net.toLocaleString('ru-RU')}
                </span>
                <span className="text-xl text-emerald-500 pb-0.5">₽</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-black/20 rounded-xl px-3 py-2.5">
              <p className="text-[10px] text-slate-600 mb-0.5">Доход/час</p>
              <p className="text-sm font-semibold text-white">
                {totalHours > 0 ? Math.round(net / totalHours).toLocaleString('ru-RU') : 0} ₽
              </p>
            </div>
            <div className="bg-black/20 rounded-xl px-3 py-2.5">
              <p className="text-[10px] text-slate-600 mb-0.5">За год</p>
              <p className="text-sm font-semibold text-white">
                {(net * 12).toLocaleString('ru-RU')} ₽
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Note */}
      <div className="flex items-start gap-3 bg-[#13161f] border border-[#1e2130] rounded-2xl px-5 py-4">
        <span className="w-5 h-5 flex items-center justify-center text-slate-600 flex-shrink-0 mt-0.5">
          <i className="ri-scales-line text-sm" />
        </span>
        <p className="text-xs text-slate-600 leading-relaxed">
          Расчёт является приблизительным прогнозом. Налог НПД 6% взимается с каждого поступления.
          Ставки: дневная (06:00–22:00 МСК) — 380 ₽/ч, ночная (22:00–06:00 МСК) — 420 ₽/ч.
        </p>
      </div>

    </div>
  );
}
